import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import { Heading } from "../../../../components/common/typography";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import AuthBrandPanel from "./auth-brand-panel";
import PasswordLoginSection from "./login/PasswordLoginSection";
import OtpLoginSection from "./login/OtpLoginSection";
import LoginSharedActions from "./login/LoginSharedActions";

type AuthTab = "login" | "signup";
type LoginMode = "password" | "otp";

const LOGIN_TABS = [
  { value: "password", label: "Email sign in" },
  { value: "otp", label: "Phone sign in" },
] as const;

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  initialTab?: AuthTab;
}

export function AuthDialog({ open, onClose, initialTab = "login" }: AuthDialogProps) {
  const [mode, setMode] = useState<LoginMode>("password");

  useEffect(() => {
    if (open) setMode("password");
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const isPasswordLogin = mode === "password";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-6 sm:p-10"
    >
      <button
        type="button"
        aria-label="Close sign-in dialog"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/40"
      />

      <div className="relative z-10 flex h-full w-full max-w-[1280px] overflow-hidden rounded-3xl bg-bgWhite shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-5 top-5 z-20 rounded-full bg-bgWhite/80 p-1.5 text-textMuted backdrop-blur transition-colors hover:bg-bgLight hover:text-textDark"
        >
          <IconX size={20} />
        </button>

        <div className="hidden h-full w-1/2 shrink-0 lg:block">
          <AuthBrandPanel />
        </div>

        <section className="flex flex-1 justify-center overflow-y-auto px-6 py-16 sm:px-12">
          <div className="flex w-full max-w-[528px] flex-col items-center gap-6 self-start">
            <Heading variant="h2" tone="dark">
              <span id="auth-dialog-title">Sign in</span>
            </Heading>
            <SegmentedControl
              aria-label="Sign in method"
              options={LOGIN_TABS}
              value={mode}
              onChange={setMode}
            />
            {isPasswordLogin ? <PasswordLoginSection /> : <OtpLoginSection />}
            <LoginSharedActions isPasswordLogin={isPasswordLogin} />
          </div>
        </section>
      </div>
    </div>
  );
}

export default AuthDialog;
